export interface HuntableGameSpecies {
  /** Localized display name shown in pickers (e.g. "Moose"). */
  readonly name: string
  readonly classes: readonly string[]
  /**
   * The catalog entry's raw, locale-independent base name (Remote Config
   * `name.base`). This is the value written to a game pin's `games[].name`
   * (the wire identity mobile keys on), NOT the localized display `name`.
   */
  readonly baseName?: string
  /**
   * Kingdom of the species, mapped from the Remote Config `type` field. Only
   * 'mammal' / 'bird' are carried through; any other/absent value leaves this
   * undefined. A game pin must carry a valid `gameType` — mobile throws on an
   * unknown one — so the downed-game picker offers only species that have it.
   */
  readonly gameType?: 'mammal' | 'bird'
}

// Bundled fallback catalog used offline / when Remote Config is unavailable.
// These entries deliberately carry NO `baseName`/`gameType`: they exist only so
// the hunt-plan wizard still shows species names without a network round-trip.
// Because they lack a `gameType`, they are (correctly) NOT selectable in the
// downed-game picker — recording downed game needs the live Remote Config
// catalog so the pin gets a valid wire name + gameType. See
// `selectableDownedGameSpecies`.
/**
 * Offline fallback for the Remote Config `gameNames` catalog, used only when
 * that fetch fails. Class names here must match the ones Remote Config carries,
 * because they are written to the plan as-is and read back by mobile: the
 * fallow-deer calf class is "Fawn" there (subtype `fallow_deer_fawn`), not
 * "Calf". Moose still offers "Heifer" and "Young bull", which Remote Config
 * does not — an extra offline option, not a wrong name.
 */
export const HUNTABLE_GAME_CATALOG: readonly HuntableGameSpecies[] = [
  { name: 'Moose',       classes: ['Calf', 'Heifer', 'Cow', 'Young bull', 'Bull'] },
  { name: 'Roe deer',    classes: ['Kid', 'Doe', 'Buck'] },
  { name: 'Wild boar',   classes: ['Piglet', 'Sow', 'Boar'] },
  { name: 'Fallow deer', classes: ['Fawn', 'Doe', 'Buck'] },
  { name: 'Red deer',    classes: ['Calf', 'Hind', 'Stag'] },
  { name: 'Hare',        classes: [] },
  { name: 'Fox',         classes: [] },
  { name: 'Badger',      classes: [] },
  { name: 'Pheasant',    classes: [] },
]

/**
 * Localized display name for a species wire id (a game pin's `games[].name`,
 * which is also the `species` key the report-stats API returns). Resolves via
 * the catalog's `baseName`, falling back to the raw id when the (offline or
 * partial) catalog can't resolve it — the same behaviour the downed-game picker
 * relies on, kept in one place so every surface names a species identically.
 */
export function speciesDisplayName(
  catalog: readonly HuntableGameSpecies[],
  wireName: string,
): string {
  return catalog.find((s) => s.baseName === wireName)?.name ?? wireName
}

/**
 * A species is eligible for the downed-game picker only when it can produce a
 * valid pin: both a wire `baseName` (→ `games[].name`) and a `gameType`
 * (→ `games[].gameType`) must be present. Species missing either (e.g. the
 * bundled fallback catalog, or a Remote Config entry with an unrecognized
 * `type`) are excluded so we never write a pin mobile would reject.
 */
export function selectableDownedGameSpecies(
  catalog: readonly HuntableGameSpecies[],
): HuntableGameSpecies[] {
  return catalog.filter(
    (s): s is HuntableGameSpecies & { baseName: string; gameType: 'mammal' | 'bird' } =>
      !!s.baseName && !!s.gameType,
  )
}
